import { getRecurrenceDatesForDay } from '../../../../../utils/recurrence';
import React from 'react';
import {
  DEFAULT_RSVP_REMINDER_OFFSETS,
  DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES,
  DEFAULT_RSVP_REMINDER_OFFSET_MINUTES,
  MAX_RSVP_REMINDER_COUNT,
  getRsvpReminderSendAt,
  normalizeRsvpReminderOffsets,
} from '../../../../../utils/rsvpDeadline';

const MIN_REMINDER_OFFSET_MINUTES = 30;

function splitMinutes(value, fallback) {
  const total = Math.max(1, Number(value || fallback) || fallback);
  const days = Math.floor(total / 1440);
  const afterDays = total - days * 1440;
  const hours = Math.floor(afterDays / 60);
  const rawMinutes = afterDays - hours * 60;
  const minutes = [0, 15, 30, 45].reduce((closest, option) => (
    Math.abs(option - rawMinutes) < Math.abs(closest - rawMinutes) ? option : closest
  ), 0);
  return { days, hours, minutes };
}

function OffsetPartsInput({ legend, name, value, onChange, fallback, hint, minTotalMinutes = 1 }) {
  const parts = splitMinutes(value, fallback);
  const handlePartChange = (partName, nextValue) => {
    const nextParts = {
      ...parts,
      [partName]: Math.max(0, Number(nextValue) || 0),
    };
    const totalMinutes = Math.max(minTotalMinutes, (nextParts.days * 1440) + (nextParts.hours * 60) + nextParts.minutes);
    onChange({
      target: {
        name,
        value: String(totalMinutes),
        type: 'number',
      },
    });
  };
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="field-label">{legend}</legend>
      <div className="form-grid">
        <div className="form-field">
          <span className="field-label">Days</span>
          <input
            type="number"
            min="0"
            inputMode="numeric"
            value={parts.days}
            onChange={(event) => handlePartChange('days', event.target.value)}
            className="input"
          />
        </div>
        <div className="form-field">
          <span className="field-label">Hours</span>
          <input
            type="number"
            min="0"
            max="23"
            inputMode="numeric"
            value={parts.hours}
            onChange={(event) => handlePartChange('hours', event.target.value)}
            className="input"
          />
        </div>
        <div className="form-field">
          <span className="field-label">Minutes</span>
          <select
            value={parts.minutes}
            onChange={(event) => handlePartChange('minutes', event.target.value)}
            className="input"
          >
            <option value="0">0</option>
            {minTotalMinutes <= 15 ? <option value="15">15</option> : null}
            <option value="30">30</option>
            <option value="45">45</option>
          </select>
        </div>
      </div>
      {hint ? <small className="text-muted">{hint}</small> : null}
    </fieldset>
  );
}

function formatDateTime(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  return value.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseLocalDateTime(dateValue, timeValue = '00:00') {
  if (typeof dateValue !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return null;
  const time = typeof timeValue === 'string' && /^\d{2}:\d{2}$/.test(timeValue) ? timeValue : '00:00';
  const parsed = new Date(`${dateValue}T${time}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getUpcomingSeriesOccurrenceStarts(form, now, limit = 24) {
  const safeNow = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  return getRecurrenceDatesForDay({
    now: safeNow,
    dayCode: (form.dayOfWeek || 'Wednesday').slice(0, 2).toUpperCase(),
    recurrence: {
      frequency: form.frequency || 'WEEKLY',
      weekOfMonth: form.weekOfMonth || 'First',
      time: form.time || '09:00',
      interval: form.recurrenceInterval || 1,
      startDate: form.recurrenceStartDate,
      endDate: form.recurrenceEndDate,
    },
    maxOccurrences: limit + 1,
  }).filter(start => start >= safeNow).slice(0, limit);
}

function getNextSeriesOccurrenceStart(form, now) {
  return getUpcomingSeriesOccurrenceStarts(form, now, 1)[0] || null;
}

function getPreviewStart({ form, isRecurring, selectedOccurrenceId, now }) {
  const occurrenceDate = selectedOccurrenceId || form.occurrenceId || '';
  if (occurrenceDate) {
    return {
      label: 'Example: selected occurrence',
      start: parseLocalDateTime(occurrenceDate, form.time || form.recurrence?.time || '00:00'),
    };
  }
  if (isRecurring) {
    return {
      label: 'Example: next occurrence',
      start: getNextSeriesOccurrenceStart(form, now),
    };
  }
  return {
    label: 'Example: this event',
    start: parseLocalDateTime(form.date, form.time || '00:00'),
  };
}

function RsvpDeadlinePreview({ form, isRecurring, selectedOccurrenceId, now }) {
  if (!form.rsvpDeadlineEnabled) return null;
  const { label, start } = getPreviewStart({ form, isRecurring, selectedOccurrenceId, now });
  if (!start) {
    return <small className="text-muted">Add a valid event date and time to preview the RSVP deadline.</small>;
  }

  const deadlineOffset = Number(form.rsvpDeadlineOffsetMinutes) || DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES;
  const deadlineAt = new Date(start.getTime() - deadlineOffset * 60 * 1000);
  const reminderOffsets = normalizeRsvpReminderOffsets(form.rsvpReminderOffsetMinutesList || [form.rsvpReminderOffsetMinutes]);
  const finalOffset = reminderOffsets[reminderOffsets.length - 1];
  const nowMs = now instanceof Date && !Number.isNaN(now.getTime()) ? now.getTime() : Date.now();
  const seriesStarts = isRecurring && !selectedOccurrenceId && !form.occurrenceId
    ? getUpcomingSeriesOccurrenceStarts(form, now, 36)
    : [];
  const reminderPreviews = form.rsvpReminderEnabled ? reminderOffsets.map((offset) => {
    const initialReminderAt = getRsvpReminderSendAt(deadlineAt, offset);
    if (!seriesStarts.length || (initialReminderAt && initialReminderAt.getTime() > nowMs)) {
      return {
        offset,
        reminderAt: initialReminderAt,
        deadlineAt,
        start,
        rollsForward: false,
      };
    }
    const nextStart = seriesStarts.find((seriesStart) => {
      const nextDeadlineAt = new Date(seriesStart.getTime() - deadlineOffset * 60 * 1000);
      const nextReminderAt = getRsvpReminderSendAt(nextDeadlineAt, offset);
      return nextReminderAt && nextReminderAt.getTime() > nowMs;
    });
    if (!nextStart) {
      return {
        offset,
        reminderAt: null,
        deadlineAt: null,
        start: null,
        rollsForward: true,
      };
    }
    const nextDeadlineAt = new Date(nextStart.getTime() - deadlineOffset * 60 * 1000);
    return {
      offset,
      reminderAt: getRsvpReminderSendAt(nextDeadlineAt, offset),
      deadlineAt: nextDeadlineAt,
      start: nextStart,
      rollsForward: nextStart.getTime() !== start.getTime(),
    };
  }) : [];

  return (
    <div
      style={{
        border: '1px solid #d1d5db',
        borderRadius: 8,
        padding: '0.75rem',
        display: 'grid',
        gap: 8,
      }}
    >
      <div className="field-label">{label}</div>
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
          gap: 8,
        }}
      >
        <div>
          <dt className="text-muted" style={{ fontSize: 12 }}>Event starts</dt>
          <dd style={{ margin: 0, fontWeight: 600 }}>{formatDateTime(start)}</dd>
        </div>
        <div>
          <dt className="text-muted" style={{ fontSize: 12 }}>RSVPs close</dt>
          <dd style={{ margin: 0, fontWeight: 600 }}>{formatDateTime(deadlineAt)}</dd>
        </div>
        {reminderPreviews.map((preview, index) => (
          <div key={`${preview.offset}-${index}`}>
            <dt className="text-muted" style={{ fontSize: 12 }}>
              {preview.offset === finalOffset ? 'Final reminder sends' : `Reminder ${index + 1} sends`}
            </dt>
            <dd style={{ margin: 0, fontWeight: 600 }}>
              {preview.reminderAt ? formatDateTime(preview.reminderAt) : 'No future matching occurrence'}
            </dd>
            {preview.rollsForward && preview.start ? (
              <dd className="text-muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
                Starts applying from {formatDateTime(preview.start)}
              </dd>
            ) : null}
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function RsvpDeadlineSection({ form, onChange, isRecurring, selectedOccurrenceId = '', now = new Date() }) {
  if (form.attendanceMode !== 'rsvp') return null;
  const reminderOffsets = normalizeRsvpReminderOffsets(form.rsvpReminderOffsetMinutesList || [form.rsvpReminderOffsetMinutes]);
  const updateReminderOffsets = (nextOffsets) => {
    onChange({
      target: {
        name: 'rsvpReminderOffsetMinutesList',
        value: normalizeRsvpReminderOffsets(nextOffsets),
      },
    });
  };
  const addReminderOffset = () => {
    const nextDefault = DEFAULT_RSVP_REMINDER_OFFSETS.find((offset) => !reminderOffsets.includes(offset))
      || Math.max(MIN_REMINDER_OFFSET_MINUTES, reminderOffsets[reminderOffsets.length - 1] - 30);
    updateReminderOffsets([...reminderOffsets, nextDefault]);
  };
  const updateReminderOffset = (index, value) => {
    const nextOffsets = reminderOffsets.map((offset, offsetIndex) => (
      offsetIndex === index ? Number(value) || DEFAULT_RSVP_REMINDER_OFFSET_MINUTES : offset
    ));
    updateReminderOffsets(nextOffsets);
  };
  const removeReminderOffset = (index) => {
    updateReminderOffsets(reminderOffsets.filter((_, offsetIndex) => offsetIndex !== index));
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="checkbox-row">
        <input
          type="checkbox"
          id="rsvpDeadlineEnabled"
          name="rsvpDeadlineEnabled"
          checked={!!form.rsvpDeadlineEnabled}
          onChange={onChange}
        />
        <label htmlFor="rsvpDeadlineEnabled">Close RSVPs at a deadline</label>
      </div>

      {form.rsvpDeadlineEnabled ? (
        <>
          <OffsetPartsInput
            legend={isRecurring ? 'Close RSVPs before each event' : 'Close RSVPs before the event'}
            name="rsvpDeadlineOffsetMinutes"
            value={form.rsvpDeadlineOffsetMinutes}
            fallback={DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES}
            onChange={onChange}
            hint={isRecurring
              ? 'Set how long before each event date starts RSVPs should close.'
              : 'Set how long before this event starts RSVPs should close.'}
          />

          <RsvpDeadlinePreview
            form={form}
            isRecurring={isRecurring}
            selectedOccurrenceId={selectedOccurrenceId}
            now={now}
          />

          <div className="checkbox-row">
            <input
              type="checkbox"
              id="rsvpReminderEnabled"
              name="rsvpReminderEnabled"
              checked={!!form.rsvpReminderEnabled}
              onChange={onChange}
            />
            <label htmlFor="rsvpReminderEnabled">Send a reminder before the deadline</label>
          </div>

          {form.rsvpReminderEnabled ? (
            <div style={{ display: 'grid', gap: 10 }}>
              {reminderOffsets.map((offset, index) => (
                <div
                  key={`${offset}-${index}`}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: '0.75rem',
                    display: 'grid',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <div className="field-label">{offset === reminderOffsets[reminderOffsets.length - 1] ? 'Final reminder' : `Reminder ${index + 1}`}</div>
                    {reminderOffsets.length > 1 ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => removeReminderOffset(index)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <OffsetPartsInput
                    legend="Send before deadline"
                    name={`rsvpReminderOffsetMinutesList-${index}`}
                    value={offset}
                    fallback={Math.max(DEFAULT_RSVP_REMINDER_OFFSET_MINUTES, MIN_REMINDER_OFFSET_MINUTES)}
                    onChange={(event) => updateReminderOffset(index, event.target.value)}
                    minTotalMinutes={MIN_REMINDER_OFFSET_MINUTES}
                    hint={index === reminderOffsets.length - 1 ? 'The final reminder is the one closest to the RSVP deadline.' : null}
                  />
                </div>
              ))}
              {reminderOffsets.length < MAX_RSVP_REMINDER_COUNT ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={addReminderOffset}
                  style={{ justifySelf: 'start' }}
                >
                  Add reminder
                </button>
              ) : null}
              <small className="text-muted">Members with no response will be reminded before RSVPs close. Minimum 30 minutes.</small>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
